/**
 * Theme application. Delegates the attribute write to the frozen design-tokens
 * accessor `applyThemeAttributes` and resolves the "system" preference against
 * the OS setting. There is a single brand skin, so no brand argument (ADR 0012).
 */
import type { ThemeMode, ThemePreference } from "@mb-group/shared-types";
import { applyThemeAttributes } from "@mb-group/design-tokens";

const STORAGE_KEY = "mb-theme-preference";

export function resolveTheme(pref: ThemePreference): ThemeMode {
	if (pref === "light" || pref === "dark") return pref;
	if (typeof window !== "undefined" && window.matchMedia) {
		return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
	}
	return "light";
}

export function readStoredPreference(): ThemePreference {
	try {
		const raw = localStorage.getItem(STORAGE_KEY);
		if (raw === "light" || raw === "dark" || raw === "system") return raw;
	} catch {
		/* localStorage unavailable */
	}
	return "system";
}

export function storePreference(pref: ThemePreference): void {
	try {
		localStorage.setItem(STORAGE_KEY, pref);
	} catch {
		/* ignore */
	}
}

export function applyTheme(pref: ThemePreference): ThemeMode {
	const mode = resolveTheme(pref);
	applyThemeAttributes(mode);
	return mode;
}

/** Subscribe to OS theme changes (only relevant while preference === "system"). */
export function onSystemThemeChange(cb: (mode: ThemeMode) => void): () => void {
	if (typeof window === "undefined" || !window.matchMedia) return () => {};
	const mql = window.matchMedia("(prefers-color-scheme: dark)");
	const handler = () => cb(mql.matches ? "dark" : "light");
	mql.addEventListener("change", handler);
	return () => mql.removeEventListener("change", handler);
}
